import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Edit3, Printer, X } from 'lucide-react';
import { updateJob } from '../../redux/features/jobSlice';
import { fetchCompanyById } from '../../redux/api';
import SpinTaskerLogo from '../shared/SpinTaskerLogo';
import { selectCompanyName } from '../../utils/selectorUtils';
import { formatDisplayValue, parseMoneyAmountSR } from '../../utils/displayUtils';
import {
  DEFAULT_PRINT_FIELD_VISIBILITY,
  extractVatFromGross,
  formatWorkOrderDateTime,
  formatWorkOrderDuration,
  formatWorkOrderMoney,
  getDefaultVatRate,
  getSparePartLineGross,
  getWorkOrderInterventionDate,
  getWorkOrderTravelSeconds,
  getWorkOrderWorkSeconds,
  parseVatRateInput,
  formatWorkOrderNumber,
  resolveWorkOrderCompanyDetails,
  resolveWorkOrderWorkerName
} from '../../utils/workOrderPrintUtils';

const PRINT_FIELD_OPTIONS = [
  ['client', 'Klijent'],
  ['address', 'Adresa'],
  ['contact', 'Kontakt (telefon/email)'],
  ['device', 'Uređaj'],
  ['issue', 'Prijavljen kvar'],
  ['workDescription', 'Opis rada'],
  ['usedSpareParts', 'Rezervni delovi'],
  ['materialsUsed', 'Dodatni materijali'],
  ['serviceCharged', 'Naplaćeno klijentu'],
  ['costSummary', 'Obračun troškova'],
  ['vat', 'PDV obračun'],
  ['bankAccount', 'Žiro račun'],
  ['amortization', 'Amortizacija / interni trošak'],
  ['times', 'Vreme puta i rada'],
  ['additionalNotes', 'Napomene']
];

const PrintMeta = ({ label, value }) => (
  <div className="print-meta-cell">
    <div className="print-kicker">{label}</div>
    <div className="print-meta-value">{value || '—'}</div>
  </div>
);

const PrintRow = ({ label, value }) => {
  if (value == null || value === '' || value === '-') return null;
  return (
    <div className="print-row">
      <span className="print-row-label">{label}</span>
      <span className="print-row-value">{value}</span>
    </div>
  );
};

const PrintBlock = ({ title, children }) => {
  if (!children) return null;
  return (
    <section className="print-section">
      <h5 className="print-section-title">{title}</h5>
      <div className="print-section-body">{children}</div>
    </section>
  );
};

const VAT_STORAGE_KEY = 'workOrderVatRate';

const formatVatRateLabel = (rate) => {
  const n = parseVatRateInput(rate);
  if (!n) return '';
  return Number.isInteger(n) ? `${n}` : String(n).replace('.', ',');
};

const WorkOrderPrintModal = ({
  job,
  onClose,
  travelTimeSeconds,
  workTimeSeconds
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const storedCompanyName = useSelector(selectCompanyName);
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });

  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const [fiscalReceiptNumber, setFiscalReceiptNumber] = useState('');
  const [vatRateInput, setVatRateInput] = useState('');
  const [printAt, setPrintAt] = useState(() => new Date());
  const [printFieldVisibility, setPrintFieldVisibility] = useState({
    ...DEFAULT_PRINT_FIELD_VISIBILITY
  });
  const [companyOverride, setCompanyOverride] = useState(null);

  useEffect(() => {
    const companyId = user?.result?._id || user?.result?.companyId;
    if (!companyId) return undefined;

    let cancelled = false;
    fetchCompanyById(companyId)
      .then(({ data }) => {
        if (!cancelled && data) setCompanyOverride(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [user?.result?._id, user?.result?.companyId]);

  useEffect(() => {
    setPrintAt(new Date());
    setShowPrintSettings(false);
    if (job?.report?.fiscalReceiptNumber != null) {
      setFiscalReceiptNumber(String(job.report.fiscalReceiptNumber).trim());
    } else {
      setFiscalReceiptNumber('');
    }

    let storedVat = '';
    try {
      storedVat = localStorage.getItem(VAT_STORAGE_KEY) || '';
    } catch (e) {
      storedVat = '';
    }
    const fromJob = job?.report?.vatRate;
    const countryCode = user?.result?.countryCode || user?.result?.company?.countryCode;
    const nextVat =
      fromJob != null && fromJob !== ''
        ? String(fromJob)
        : storedVat || String(getDefaultVatRate(countryCode));
    setVatRateInput(nextVat);

    setPrintFieldVisibility({
      ...DEFAULT_PRINT_FIELD_VISIBILITY,
      vat:
        job?.report?.includeVat != null
          ? !!job.report.includeVat
          : parseVatRateInput(nextVat) > 0,
      bankAccount:
        job?.report?.includeBankAccount != null ? !!job.report.includeBankAccount : false
    });
  }, [
    job?._id,
    job?.report?.fiscalReceiptNumber,
    job?.report?.vatRate,
    job?.report?.includeVat,
    job?.report?.includeBankAccount,
    user,
    storedCompanyName
  ]);

  if (!job) return null;

  const company = {
    ...resolveWorkOrderCompanyDetails(user, storedCompanyName),
    ...(companyOverride
      ? {
          name: companyOverride.companyName || undefined,
          phone: companyOverride.phone || undefined,
          email: companyOverride.email || undefined,
          address: companyOverride.address || undefined,
          city: companyOverride.city || undefined,
          pib: companyOverride.pib || undefined,
          registrationNumber: companyOverride.registrationNumber || undefined,
          bankAccount: companyOverride.bankAccount?.trim() || undefined,
          bankName: companyOverride.bankName?.trim() || undefined
        }
      : {})
  };
  const companyName = company.name;
  const companyLocation = [company.address, company.city].filter(Boolean).join(', ');
  const companyContact = [company.phone, company.email].filter(Boolean).join(' · ');
  const companyLegal = [
    company.pib ? `PIB: ${company.pib}` : '',
    company.registrationNumber ? `MB: ${company.registrationNumber}` : ''
  ]
    .filter(Boolean)
    .join(' · ');
  const companyBankLine = printFieldVisibility.bankAccount && company.bankAccount
    ? [company.bankAccount, company.bankName ? `(${company.bankName})` : ''].filter(Boolean).join(' ')
    : '';
  const serviceGross = parseMoneyAmountSR(job.report?.serviceCharged);
  const spareParts = job.report?.usedSpareParts || [];
  const partsTotalFromLines = spareParts.reduce(
    (sum, part) => sum + getSparePartLineGross(part),
    0
  );
  const materialsCost =
    (Number(job.report?.reversiCost) || 0) + (Number(job.report?.additionalMaterialsCost) || 0) ||
    partsTotalFromLines;
  const amortizationCost =
    Number(job.report?.amortizationCost) ||
    parseMoneyAmountSR(job.report?.amortization) ||
    0;
  const totalCost =
    Number(job.report?.totalCost) || materialsCost + amortizationCost;
  const showCostSummary =
    printFieldVisibility.costSummary &&
    (materialsCost > 0 || amortizationCost > 0 || totalCost > 0);
  const showAmortizationLine =
    printFieldVisibility.amortization && amortizationCost > 0;
  const workerName = resolveWorkOrderWorkerName(job, workers, partners);
  const interventionDate = getWorkOrderInterventionDate(job);
  const jobNumber = formatWorkOrderNumber(job);
  const travelSeconds = getWorkOrderTravelSeconds(job, travelTimeSeconds);
  const workSeconds = getWorkOrderWorkSeconds(job, workTimeSeconds);
  const deviceLabel = [job.deviceType, job.deviceBrand, job.deviceModel].filter(Boolean).join(' ');
  const hasSerial = job.deviceSerialNumber && job.deviceSerialNumber !== 'N/A';
  const showSpareParts = printFieldVisibility.usedSpareParts && spareParts.length > 0;
  const clientEmail =
    job.clientEmail && job.clientEmail !== 'no-email@example.com' ? job.clientEmail : '';
  const showClientSection =
    (printFieldVisibility.client && formatDisplayValue(job.clientName) !== '-') ||
    (printFieldVisibility.address && formatDisplayValue(job.clientAddress) !== '-') ||
    (printFieldVisibility.contact && !!(job.clientPhone || clientEmail));
  const hasTrackedTimes = travelSeconds > 0 || workSeconds > 0;
  const showDeviceSection =
    (printFieldVisibility.device && !!(deviceLabel || hasSerial)) ||
    (printFieldVisibility.times && hasTrackedTimes);
  const vatRate = parseVatRateInput(vatRateInput);
  const showVat = printFieldVisibility.vat && vatRate > 0;
  const serviceVat = extractVatFromGross(serviceGross, vatRate);
  const partLines = spareParts.map((part) => {
    const gross = getSparePartLineGross(part);
    return { part, ...extractVatFromGross(gross, vatRate) };
  });
  const partsVat = partLines.reduce(
    (acc, line) => ({
      gross: acc.gross + line.gross,
      net: acc.net + line.net,
      vat: acc.vat + line.vat
    }),
    { gross: 0, net: 0, vat: 0 }
  );
  const showServiceCharge = printFieldVisibility.serviceCharged && job.report?.serviceCharged != null && job.report?.serviceCharged !== '';
  const totalVat = {
    net: (showServiceCharge ? serviceVat.net : 0) + (showSpareParts ? partsVat.net : 0),
    vat: (showServiceCharge ? serviceVat.vat : 0) + (showSpareParts ? partsVat.vat : 0),
    gross: (showServiceCharge ? serviceVat.gross : 0) + (showSpareParts ? partsVat.gross : 0)
  };

  const handlePrint = () => {
    const vatToSave = parseVatRateInput(vatRateInput);
    try {
      if (vatToSave > 0) localStorage.setItem(VAT_STORAGE_KEY, String(vatToSave));
    } catch (e) {
      // ignore storage errors
    }
    if (job._id) {
      const reportUpdate = {
        ...(job.report || {}),
        fiscalReceiptNumber: (fiscalReceiptNumber || '').trim(),
        vatRate: vatToSave,
        includeVat: !!printFieldVisibility.vat,
        includeBankAccount: !!printFieldVisibility.bankAccount
      };
      dispatch(updateJob({ id: job._id, jobData: { report: reportUpdate } }));
    }
    const prevShowSettings = showPrintSettings;
    setShowPrintSettings(false);
    setPrintAt(new Date());
    setTimeout(() => {
      window.print();
      setShowPrintSettings(prevShowSettings);
    }, 0);
  };

  return (
    <>
      <div className="print-modal-overlay fixed inset-0 z-[80] p-4 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm">
        <div className="print-modal-shell w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-700/70 bg-slate-900/95 p-4 sm:p-6 shadow-2xl">
          <div className="print-ui-only flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">Radni nalog</h3>
              <p className="text-xs text-gray-400 mt-1">
                Pregled za štampu. Polja za čekiranje služe samo za pripremu i neće se pojaviti na štampi.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
              title="Zatvori"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="print-preview-content print-doc">
            <header className="print-doc-header">
              <div className="print-doc-brand">
                <div className="print-doc-company">{companyName || 'Servis'}</div>
                {companyLocation ? (
                  <div className="print-doc-company-meta">{companyLocation}</div>
                ) : null}
                {companyContact ? (
                  <div className="print-doc-company-meta">{companyContact}</div>
                ) : null}
                {companyLegal ? (
                  <div className="print-doc-company-meta">{companyLegal}</div>
                ) : null}
                {companyBankLine ? (
                  <div className="print-doc-company-meta">Žiro račun: {companyBankLine}</div>
                ) : null}
                <div className="print-doc-subtitle">Servisni radni nalog</div>
              </div>
              <div className="print-doc-badge">
                <div className="print-kicker">Broj naloga</div>
                <div className="print-doc-number">{jobNumber || '—'}</div>
              </div>
            </header>

            <div className="print-meta-row">
              <PrintMeta label="Radio" value={workerName} />
              <PrintMeta label="Datum intervencije" value={formatWorkOrderDateTime(interventionDate)} />
              <PrintMeta label="Datum štampe" value={formatWorkOrderDateTime(printAt)} />
            </div>

            <div className="print-two-col">
              {showClientSection ? (
                <PrintBlock title="Klijent">
                  {printFieldVisibility.client ? (
                    <PrintRow label="Ime" value={formatDisplayValue(job.clientName)} />
                  ) : null}
                  {printFieldVisibility.address ? (
                    <PrintRow label="Adresa" value={formatDisplayValue(job.clientAddress)} />
                  ) : null}
                  {printFieldVisibility.contact ? (
                    <>
                      <PrintRow label="Telefon" value={job.clientPhone} />
                      <PrintRow label="Email" value={clientEmail} />
                    </>
                  ) : null}
                </PrintBlock>
              ) : null}

              {showDeviceSection ? (
                <PrintBlock title="Uređaj i vreme">
                  {printFieldVisibility.device ? (
                    <>
                      <PrintRow label="Uređaj" value={deviceLabel} />
                      {hasSerial ? <PrintRow label="Serijski broj" value={job.deviceSerialNumber} /> : null}
                    </>
                  ) : null}
                  {printFieldVisibility.times && hasTrackedTimes ? (
                    <>
                      {travelSeconds > 0 ? (
                        <PrintRow label="Vreme puta" value={formatWorkOrderDuration(travelSeconds)} />
                      ) : null}
                      {workSeconds > 0 ? (
                        <PrintRow label="Vreme rada" value={formatWorkOrderDuration(workSeconds)} />
                      ) : null}
                    </>
                  ) : null}
                </PrintBlock>
              ) : null}
            </div>

            {printFieldVisibility.issue && job.issueDescription ? (
              <PrintBlock title="Prijavljen kvar">
                <p className="print-prose">{job.issueDescription}</p>
              </PrintBlock>
            ) : null}

            {printFieldVisibility.workDescription && job.report?.workDescription ? (
              <PrintBlock title="Opis izvršenog rada">
                <p className="print-prose">{job.report.workDescription}</p>
              </PrintBlock>
            ) : null}

            {showSpareParts ? (
              <PrintBlock title="Rezervni delovi">
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>Naziv</th>
                      <th>Šifra</th>
                      <th className="print-num">Kol.</th>
                      {showVat ? (
                        <>
                          <th className="print-num">Osnova</th>
                          <th className="print-num">PDV {formatVatRateLabel(vatRate)}%</th>
                          <th className="print-num">Ukupno</th>
                        </>
                      ) : (
                        <th className="print-num">Iznos</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {partLines.map((line, idx) => (
                      <tr key={idx}>
                        <td>{line.part.name || '—'}</td>
                        <td>{line.part.code || '—'}</td>
                        <td className="print-num">{line.part.quantity || 0}</td>
                        {showVat ? (
                          <>
                            <td className="print-num">{formatWorkOrderMoney(line.net)}</td>
                            <td className="print-num">{formatWorkOrderMoney(line.vat)}</td>
                            <td className="print-num">{formatWorkOrderMoney(line.gross)}</td>
                          </>
                        ) : (
                          <td className="print-num">{formatWorkOrderMoney(line.gross)}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </PrintBlock>
            ) : null}

            {printFieldVisibility.materialsUsed && job.report?.materialsUsed ? (
              <PrintBlock title="Dodatni materijali">
                <p className="print-prose">{job.report.materialsUsed}</p>
              </PrintBlock>
            ) : null}

            {printFieldVisibility.additionalNotes && job.report?.additionalNotes ? (
              <PrintBlock title="Napomene">
                <p className="print-prose">{job.report.additionalNotes}</p>
              </PrintBlock>
            ) : null}

            {showCostSummary ? (
              <PrintBlock title="Obračun troškova">
                {materialsCost > 0 ? (
                  <PrintRow label="Materijal" value={formatWorkOrderMoney(materialsCost)} />
                ) : null}
                {amortizationCost > 0 ? (
                  <PrintRow label="Amortizacija" value={formatWorkOrderMoney(amortizationCost)} />
                ) : null}
                <PrintRow label="Ukupan trošak" value={formatWorkOrderMoney(totalCost)} />
              </PrintBlock>
            ) : null}

            <div className="print-summary">
              {showVat && (showServiceCharge || (showSpareParts && partsVat.gross > 0)) ? (
                <div className="print-vat-box">
                  <div className="print-kicker">Obračun PDV-a {formatVatRateLabel(vatRate)}% — izdvojen iz naplate</div>
                  {showServiceCharge ? (
                    <div className="print-vat-group">
                      <div className="print-vat-group-title">Usluga</div>
                      <div className="print-vat-line">
                        <span>Osnova bez PDV-a</span>
                        <span>{formatWorkOrderMoney(serviceVat.net)}</span>
                      </div>
                      <div className="print-vat-line">
                        <span>PDV {formatVatRateLabel(vatRate)}%</span>
                        <span>{formatWorkOrderMoney(serviceVat.vat)}</span>
                      </div>
                      <div className="print-vat-line print-vat-line-strong">
                        <span>Ukupno usluga</span>
                        <span>{formatWorkOrderMoney(serviceVat.gross)}</span>
                      </div>
                    </div>
                  ) : null}
                  {showSpareParts && partsVat.gross > 0 ? (
                    <div className="print-vat-group">
                      <div className="print-vat-group-title">Rezervni delovi</div>
                      <div className="print-vat-line">
                        <span>Osnova bez PDV-a</span>
                        <span>{formatWorkOrderMoney(partsVat.net)}</span>
                      </div>
                      <div className="print-vat-line">
                        <span>PDV {formatVatRateLabel(vatRate)}%</span>
                        <span>{formatWorkOrderMoney(partsVat.vat)}</span>
                      </div>
                      <div className="print-vat-line print-vat-line-strong">
                        <span>Ukupno delovi</span>
                        <span>{formatWorkOrderMoney(partsVat.gross)}</span>
                      </div>
                    </div>
                  ) : null}
                  <div className="print-vat-total">
                    <div className="print-vat-line">
                      <span>Ukupna osnova</span>
                      <span>{formatWorkOrderMoney(totalVat.net)}</span>
                    </div>
                    <div className="print-vat-line">
                      <span>Ukupan PDV</span>
                      <span>{formatWorkOrderMoney(totalVat.vat)}</span>
                    </div>
                    <div className="print-vat-line print-grand">
                      <span>Ukupno sa PDV-om</span>
                      <strong>{formatWorkOrderMoney(totalVat.gross)}</strong>
                    </div>
                  </div>
                </div>
              ) : showServiceCharge ? (
                <div className="print-total">
                  <span>Naplaćeno klijentu</span>
                  <strong>{formatWorkOrderMoney(serviceGross)}</strong>
                </div>
              ) : null}
              {showAmortizationLine && !showCostSummary ? (
                <div className="print-note-line">
                  Amortizacija / interni trošak: {formatWorkOrderMoney(amortizationCost)}
                </div>
              ) : null}
            </div>

            <div className="print-closing">
              <div className="print-fiscal">
                <div className="print-kicker">Broj fiskalnog računa</div>
                <div className="print-fiscal-line">{fiscalReceiptNumber || '\u00A0'}</div>
              </div>
              <div className="print-sign">
                <div className="print-kicker">Potpis stranke</div>
                {job.report?.customerSignature ? (
                  <img
                    src={job.report.customerSignature}
                    alt="Potpis stranke"
                    className="print-sign-img"
                  />
                ) : (
                  <div className="print-sign-blank" />
                )}
              </div>
            </div>

            <footer className="print-doc-footer">
              <span className="print-by">by</span>
              <SpinTaskerLogo
                asLink={false}
                animate={false}
                headingClassName="print-logo-text"
                atClassName="print-logo-at"
              />
            </footer>
          </div>

          <div className="print-ui-only mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-white">Podešavanje prikaza</h4>
              <button
                type="button"
                onClick={() => setShowPrintSettings((prev) => !prev)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-600"
              >
                <Edit3 className="h-3 w-3" />
                {showPrintSettings ? 'Sakrij' : 'Uredi'}
              </button>
            </div>

            {showPrintSettings && (
              <div className="space-y-3">
                <div className="text-xs">
                  <label className="block font-medium text-gray-200 mb-1">Broj fiskalnog računa</label>
                  <input
                    type="text"
                    value={fiscalReceiptNumber}
                    onChange={(e) => setFiscalReceiptNumber(e.target.value)}
                    placeholder="Unesite broj fiskalnog računa"
                    className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-200">
                  {PRINT_FIELD_OPTIONS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!printFieldVisibility[key]}
                        onChange={(e) =>
                          setPrintFieldVisibility((prev) => ({
                            ...prev,
                            [key]: e.target.checked
                          }))
                        }
                        className="w-3 h-3 text-blue-500 bg-gray-700 border-gray-600 rounded"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>

                {printFieldVisibility.vat ? (
                  <div className="text-xs">
                    <label className="block font-medium text-gray-200 mb-1">PDV stopa (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={vatRateInput}
                      onChange={(e) => setVatRateInput(e.target.value)}
                      placeholder="npr. 20 za Srbiju, 17 za BiH"
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="print-ui-only flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="w-full sm:w-1/2 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Printer className="h-4 w-4" />
              Štampaj
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-1/2 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm"
            >
              <X className="h-4 w-4" />
              Zatvori
            </button>
          </div>
        </div>
      </div>
      <style>
        {`
          .print-doc {
            background: #fff;
            color: #111827;
            max-width: 860px;
            margin: 0 auto 16px;
            padding: 28px 32px;
            border: 1px solid #e5e7eb;
            border-radius: 16px;
            box-shadow: 0 25px 50px -12px rgb(15 23 42 / 0.25);
            font-family: Georgia, 'Times New Roman', Times, serif;
            line-height: 1.45;
          }

          .print-kicker {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 10px;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: #6b7280;
            font-weight: 700;
            margin-bottom: 4px;
          }

          .print-doc-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            gap: 16px;
            padding-bottom: 16px;
            border-bottom: 3px solid #166534;
          }

          .print-doc-company {
            font-size: 26px;
            font-weight: 700;
            letter-spacing: -0.03em;
            color: #111827;
            line-height: 1.15;
          }

          .print-doc-company-meta {
            margin-top: 2px;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            color: #4b5563;
            line-height: 1.35;
          }

          .print-doc-subtitle {
            margin-top: 4px;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            color: #4b5563;
          }

          .print-doc-badge {
            text-align: right;
            min-width: 160px;
          }

          .print-doc-number {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 22px;
            font-weight: 800;
            color: #166534;
            letter-spacing: 0.02em;
          }

          .print-meta-row {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 0;
            margin: 18px 0 20px;
            border: 1px solid #d1d5db;
          }

          .print-meta-cell {
            padding: 10px 12px;
            border-right: 1px solid #d1d5db;
          }

          .print-meta-cell:last-child {
            border-right: none;
          }

          .print-meta-value {
            font-size: 13px;
            font-weight: 600;
            color: #111827;
          }

          .print-two-col {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 8px;
          }

          .print-section {
            margin-bottom: 14px;
          }

          .print-section-title {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #166534;
            border-bottom: 1px solid #bbf7d0;
            padding-bottom: 4px;
            margin: 0 0 8px;
            font-weight: 700;
          }

          .print-row {
            display: grid;
            grid-template-columns: 118px 1fr;
            gap: 8px;
            padding: 3px 0;
            font-size: 13px;
          }

          .print-row-label {
            color: #6b7280;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            padding-top: 2px;
          }

          .print-row-value {
            color: #111827;
            font-weight: 600;
          }

          .print-prose {
            margin: 0;
            white-space: pre-wrap;
            font-size: 13px;
            color: #1f2937;
          }

          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
            font-family: Arial, Helvetica, sans-serif;
          }

          .print-table th {
            text-align: left;
            font-size: 10px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #4b5563;
            border-bottom: 1px solid #d1d5db;
            padding: 6px 8px;
          }

          .print-table td {
            border-bottom: 1px solid #f3f4f6;
            padding: 7px 8px;
            color: #111827;
          }

          .print-num {
            text-align: right !important;
            white-space: nowrap;
          }

          .print-summary {
            margin: 8px 0 18px;
          }

          .print-total {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #f0fdf4;
            border: 1px solid #bbf7d0;
            padding: 10px 14px;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 13px;
            color: #14532d;
          }

          .print-total strong {
            font-size: 18px;
            color: #166534;
          }

          .print-vat-box {
            font-family: Arial, Helvetica, sans-serif;
            border: 1px solid #bbf7d0;
            padding: 12px 14px;
            background: #f8faf8;
          }

          .print-vat-group {
            margin-top: 10px;
            padding-top: 8px;
            border-top: 1px solid #e5e7eb;
          }

          .print-vat-group-title {
            font-size: 11px;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: #166534;
            font-weight: 700;
            margin-bottom: 4px;
          }

          .print-vat-line {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            font-size: 12px;
            color: #374151;
            padding: 2px 0;
          }

          .print-vat-line-strong {
            font-weight: 700;
            color: #111827;
          }

          .print-vat-total {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 2px solid #166534;
          }

          .print-grand {
            margin-top: 4px;
            font-size: 14px;
            color: #14532d;
          }

          .print-grand strong {
            font-size: 18px;
            color: #166534;
          }

          .print-note-line {
            margin-top: 8px;
            font-size: 12px;
            color: #4b5563;
          }

          .print-closing {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 24px;
            align-items: end;
            margin-top: 8px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
          }

          .print-fiscal-line {
            min-height: 32px;
            border-bottom: 1px solid #9ca3af;
            font-size: 14px;
            font-weight: 600;
            padding-top: 8px;
          }

          .print-sign-img {
            max-height: 72px;
            max-width: 240px;
            object-fit: contain;
            border: 1px solid #e5e7eb;
            background: #fafafa;
            padding: 6px;
          }

          .print-sign-blank {
            height: 56px;
            border-bottom: 1px solid #9ca3af;
          }

          .print-doc-footer {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 6px;
            margin-top: 28px;
            padding-top: 12px;
            border-top: 1px solid #e5e7eb;
          }

          .print-by {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            color: #9ca3af;
          }

          .print-logo-text {
            margin: 0;
            font-size: 14px;
            font-weight: 800;
            color: #166534;
            display: flex;
            align-items: center;
            font-family: Arial, Helvetica, sans-serif;
          }

          .print-logo-at {
            color: #111827;
            margin: 0 2px;
            display: inline-block;
            font-size: 0.67em;
          }

          @media (max-width: 640px) {
            .print-doc {
              padding: 18px 16px;
            }
            .print-doc-header,
            .print-two-col,
            .print-closing,
            .print-meta-row {
              grid-template-columns: 1fr;
              display: grid;
            }
            .print-doc-header {
              display: grid;
            }
            .print-doc-badge {
              text-align: left;
            }
            .print-meta-cell {
              border-right: none;
              border-bottom: 1px solid #d1d5db;
            }
            .print-row {
              grid-template-columns: 1fr;
              gap: 2px;
            }
          }

          @media print {
            @page {
              size: A4;
              margin: 12mm;
            }

            body * {
              visibility: hidden !important;
            }

            .print-preview-content,
            .print-preview-content * {
              visibility: visible !important;
            }

            .print-ui-only {
              display: none !important;
            }

            .print-preview-content {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
              border: none !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              background: #fff !important;
            }

            .print-doc-header,
            .print-two-col,
            .print-closing,
            .print-meta-row {
              display: grid !important;
            }

            .print-doc-header {
              display: flex !important;
            }

            .print-two-col,
            .print-closing {
              grid-template-columns: 1fr 1fr !important;
            }

            .print-meta-row {
              grid-template-columns: repeat(3, 1fr) !important;
            }

            .print-total,
            .print-vat-box {
              background: #fff !important;
              border: 1px solid #166534 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
    </>
  );
};

export default WorkOrderPrintModal;
